import { useState, useEffect } from 'react';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

export default function About() {
  const [content, setContent] = useState<SiteContent>({});
  const [customFonts, setCustomFonts] = useState<CustomFont[]>([]);
  const [loading, setLoading] = useState(true);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  // Default fallback content
  const defaultContent: SiteContent = {
    about_hero_title: 'We Are Creative Duo',
    about_hero_subtitle: 'For 18 years, we\'ve been crafting compelling stories that connect brands with their audiences through the perfect blend of art direction and copywriting.',
    about_hero_image: 'https://readdy.ai/api/search-image?query=professional%20creative%20duo%20portrait%20photography%2C%20art%20director%20and%20copywriter%20working%20together%2C%20modern%20studio%20environment%2C%20clean%20minimalist%20background%2C%20professional%20business%20photography%2C%20collaborative%20workspace%2C%20natural%20lighting&width=600&height=700&seq=7&orientation=portrait',
    about_hero_title_font: 'Inter',
    about_hero_subtitle_font: 'Inter',
    about_hero_title_size: 'text-5xl md:text-6xl',
    about_hero_subtitle_size: 'text-xl',
    about_story_title: 'Our Story',
    about_story_subtitle: 'Born from a shared passion for storytelling and visual excellence',
    about_roel_title_1: 'ROEL',
    about_roel_subtitle_1: 'The Art Director',
    about_roel_text_1: 'WITH AN EYE FOR DETAIL AND A PASSION FOR VISUAL STORYTELLING, OUR ART DIRECTOR BRINGS CONCEPTS TO LIFE THROUGH COMPELLING IMAGERY, THOUGHTFUL DESIGN, AND STRATEGIC VISUAL COMMUNICATION. EVERY FRAME TELLS A STORY, EVERY COLOR CHOICE HAS PURPOSE.',
    about_roel_content_type_1: 'text',
    about_roel_logos_1: '[]',
    about_roel_title_size_1: 'text-4xl',
    about_roel_subtitle_size_1: 'text-2xl',
    about_roel_text_size_1: 'text-base',
    about_roel_title_font_1: 'Inter',
    about_roel_subtitle_font_1: 'Inter',
    about_roel_text_font_1: 'Inter',
    about_roel_title_2: 'ROEL',
    about_roel_subtitle_2: 'The Art Director',
    about_roel_text_2: 'WITH AN EYE FOR DETAIL AND A PASSION FOR VISUAL STORYTELLING, OUR ART DIRECTOR BRINGS CONCEPTS TO LIFE THROUGH COMPELLING IMAGERY, THOUGHTFUL DESIGN, AND STRATEGIC VISUAL COMMUNICATION. EVERY FRAME TELLS A STORY, EVERY COLOR CHOICE HAS PURPOSE.',
    about_roel_content_type_2: 'text',
    about_brands_logos_2: '[]',
    about_roel_title_size_2: 'text-4xl',
    about_roel_subtitle_size_2: 'text-2xl',
    about_roel_text_size_2: 'text-base',
    about_roel_title_font_2: 'Inter',
    about_roel_subtitle_font_2: 'Inter',
    about_roel_text_font_2: 'Inter',
    about_roel_title_3: 'ROEL',
    about_roel_subtitle_3: 'The Art Director',
    about_roel_text_3: 'WITH AN EYE FOR DETAIL AND A PASSION FOR VISUAL STORYTELLING, OUR ART DIRECTOR BRINGS CONCEPTS TO LIFE THROUGH COMPELLING IMAGERY, THOUGHTFUL DESIGN, AND STRATEGIC VISUAL COMMUNICATION. EVERY FRAME TELLS A STORY, EVERY COLOR CHOICE HAS PURPOSE.',
    about_roel_title_size_3: 'text-4xl',
    about_roel_subtitle_size_3: 'text-2xl',
    about_roel_text_size_3: 'text-base',
    about_roel_title_font_3: 'Inter',
    about_roel_subtitle_font_3: 'Inter',
    about_roel_text_font_3: 'Inter',
    about_values_title: 'What Drives Us',
    about_values_subtitle: 'Our core values shape every project we undertake and every relationship we build',
    about_cta_title: 'Ready to Create Something Amazing?',
    about_cta_subtitle: 'Let\'s discuss your project and explore how we can bring your vision to life through compelling storytelling and exceptional design.',
    about_cta_button1_text: 'Start a Conversation',
    about_cta_button2_text: 'View Our Process'
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch('/admin/content-api.php');
        const data = await response.json();
        
        if (data.success) {
          setContent({ ...defaultContent, ...data.data });
          
          // Fetch custom fonts if available
          if (data.custom_fonts) {
            setCustomFonts(data.custom_fonts);
          }
        } else {
          setContent(defaultContent);
        }
      } catch (error) {
        console.error('Error fetching content:', error);
        setContent(defaultContent);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Load custom fonts and Google Fonts with Safari compatibility
  useEffect(() => {
    const loadFonts = async () => {
      // Load custom fonts
      if (customFonts.length > 0) {
        const style = document.createElement('style');
        let cssRules = '';
        
        customFonts.forEach(font => {
          const sources = [];
          if (font.woff2_url) sources.push(`url('${font.woff2_url}') format('woff2')`);
          if (font.woff_url) sources.push(`url('${font.woff_url}') format('woff')`);
          if (font.ttf_url) sources.push(`url('${font.ttf_url}') format('truetype')`);
          if (font.otf_url) sources.push(`url('${font.otf_url}') format('opentype')`);
          
          if (sources.length > 0) {
            cssRules += `
              @font-face {
                font-family: '${font.font_family}';
                src: ${sources.join(', ')};
                font-display: swap;
                font-weight: normal;
                font-style: normal;
              }
            `;
          }
        });
        
        if (cssRules) {
          style.textContent = cssRules;
          document.head.appendChild(style);
        }
      }

      // Load Google Fonts for about page fonts
      const googleFontsToLoad = new Set<string>();
      
      if (content.about_hero_title_font && content.about_hero_title_font !== 'Inter' && !customFonts.find(f => f.font_family === content.about_hero_title_font)) {
        googleFontsToLoad.add(content.about_hero_title_font);
      }
      
      if (content.about_hero_subtitle_font && content.about_hero_subtitle_font !== 'Inter' && !customFonts.find(f => f.font_family === content.about_hero_subtitle_font)) {
        googleFontsToLoad.add(content.about_hero_subtitle_font);
      }

      // Add all ROEL section fonts
      [1, 2, 3].forEach(num => {
        const titleFont = content[`about_roel_title_font_${num}` as keyof SiteContent] as string;
        const subtitleFont = content[`about_roel_subtitle_font_${num}` as keyof SiteContent] as string;
        const textFont = content[`about_roel_text_font_${num}` as keyof SiteContent] as string;
        
        if (titleFont && titleFont !== 'Inter' && !customFonts.find(f => f.font_family === titleFont)) {
          googleFontsToLoad.add(titleFont);
        }
        if (subtitleFont && subtitleFont !== 'Inter' && !customFonts.find(f => f.font_family === subtitleFont)) {
          googleFontsToLoad.add(subtitleFont);
        }
        if (textFont && textFont !== 'Inter' && !customFonts.find(f => f.font_family === textFont)) {
          googleFontsToLoad.add(textFont);
        }
      });

      // Load Google Fonts with better Safari compatibility
      const loadGoogleFont = (fontName: string) => {
        return new Promise<void>((resolve) => {
          const fontNameForUrl = fontName.replace(/\s+/g, '+');
          const existingLink = document.querySelector(`link[href*="${fontNameForUrl}"]`);
          
          if (!existingLink) {
            const link = document.createElement('link');
            link.href = `https://fonts.googleapis.com/css2?family=${fontNameForUrl}:wght@400;500;600;700;800;900&display=swap`;
            link.rel = 'stylesheet';
            link.crossOrigin = 'anonymous';
            
            // Add load event listener for Safari compatibility
            link.onload = () => {
              console.log(`About page font loaded: ${fontName}`);
              resolve();
            };
            
            link.onerror = () => {
              console.warn(`Failed to load about page font: ${fontName}`);
              resolve();
            };
            
            document.head.appendChild(link);
            
            // Fallback timeout for Safari
            setTimeout(() => {
              resolve();
            }, 3000);
          } else {
            resolve();
          }
        });
      };

      // Load all Google Fonts
      const fontPromises = Array.from(googleFontsToLoad).map(fontName => loadGoogleFont(fontName));
      
      try {
        await Promise.all(fontPromises);
        
        // Force font loading check for Safari
        if (googleFontsToLoad.size > 0) {
          // Use document.fonts API if available (modern browsers including Safari)
          if ('fonts' in document) {
            try {
              await document.fonts.ready;
              console.log('All about page fonts loaded successfully');
            } catch (error) {
              console.warn('About page font loading check failed:', error);
            }
          }
        }
        
        setFontsLoaded(true);
      } catch (error) {
        console.warn('Error loading about page fonts:', error);
        setFontsLoaded(true);
      }
    };

    if (!loading && Object.keys(content).length > 0) {
      loadFonts();
    }
  }, [content, customFonts, loading]);

  if (loading || !fontsLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Helper function to render text with line breaks
  const renderTextWithBreaks = (text: string) => {
    return text.split('<br>').map((line, index, array) => (
      <span key={index}>
        {line}
        {index < array.length - 1 && <br />}
      </span>
    ));
  };

  // Parse logo arrays
  const agencyLogos = content.about_roel_logos_1 ? JSON.parse(content.about_roel_logos_1) : [];
  const brandsLogos = content.about_brands_logos_2 ? JSON.parse(content.about_brands_logos_2) : [];

  // Dynamic styles for typography with Safari font fallback
  const heroTitleStyle = {
    fontFamily: content.about_hero_title_font ? `"${content.about_hero_title_font}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const heroSubtitleStyle = {
    fontFamily: content.about_hero_subtitle_font ? `"${content.about_hero_subtitle_font}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const roelTitle1Style = {
    fontFamily: content.about_roel_title_font_1 ? `"${content.about_roel_title_font_1}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const roelSubtitle1Style = {
    fontFamily: content.about_roel_subtitle_font_1 ? `"${content.about_roel_subtitle_font_1}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const roelText1Style = {
    fontFamily: content.about_roel_text_font_1 ? `"${content.about_roel_text_font_1}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const roelTitle2Style = {
    fontFamily: content.about_roel_title_font_2 ? `"${content.about_roel_title_font_2}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const roelSubtitle2Style = {
    fontFamily: content.about_roel_subtitle_font_2 ? `"${content.about_roel_subtitle_font_2}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const roelText2Style = {
    fontFamily: content.about_roel_text_font_2 ? `"${content.about_roel_text_font_2}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const roelTitle3Style = {
    fontFamily: content.about_roel_title_font_3 ? `"${content.about_roel_title_font_3}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const roelSubtitle3Style = {
    fontFamily: content.about_roel_subtitle_font_3 ? `"${content.about_roel_subtitle_font_3}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const roelText3Style = {
    fontFamily: content.about_roel_text_font_3 ? `"${content.about_roel_text_font_3}", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif` : undefined
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Roel & Wietse Creative Team",
    "description": "Learn about our creative duo with 18 years of experience in art direction and copywriting, crafting compelling stories that connect brands with their audiences.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/about`,
    "mainEntity": {
      "@type": "Organization",
      "name": "Roel & Wietse Creative Team",
      "description": "Creative duo specializing in art direction and copywriting",
      "foundingDate": "2006",
      "founder": [
        {
          "@type": "Person",
          "name": "Roel",
          "jobTitle": "Art Director",
          "description": "With an eye for detail and a passion for visual storytelling, brings concepts to life through compelling imagery, thoughtful design, and strategic visual communication."
        },
        {
          "@type": "Person",
          "name": "Wietse",
          "jobTitle": "Copywriter",
          "description": "Crafts compelling narratives and strategic messaging that resonates with target audiences."
        }
      ],
      "knowsAbout": ["Visual Storytelling", "Art Direction", "Copywriting", "Brand Strategy", "Creative Direction"],
      "serviceArea": {
        "@type": "Place",
        "name": "Netherlands"
      }
    }
  };

  return (
    <>
      <SEOHead
        title="About Us - Roel & Wietse Creative Team"
        description="Learn about our creative duo with 18 years of experience in art direction and copywriting, crafting compelling stories that connect brands with their audiences."
        keywords="About us, Creative team, Art director, Copywriter, Creative duo, Brand storytelling, Visual communication"
        schemaData={schemaData}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center bg-white overflow-hidden">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 
                className={`${content.about_hero_title_size || 'text-5xl md:text-6xl'} font-bold text-gray-900 mb-6 leading-tight`}
                style={{ fontFamily: `'${content.about_hero_title_font || 'Inter'}', sans-serif` }}
              >
                {renderTextWithBreaks(content.about_hero_title || 'We Are Creative Duo')}
              </h1>
              <p 
                className={`${content.about_hero_subtitle_size || 'text-xl'} text-gray-600 leading-relaxed`}
                style={{ fontFamily: `'${content.about_hero_subtitle_font || 'Inter'}', sans-serif` }}
              >
                {renderTextWithBreaks(content.about_hero_subtitle || 'For 18 years, we\'ve been crafting compelling stories that connect brands with their audiences through the perfect blend of art direction and copywriting.')}
              </p>
            </div>
            
            <div className="relative">
              <img
                src={content.about_hero_image}
                alt="Creative team at work"
                className="w-full h-[500px] object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>
      
      {/* Content Sections */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-12">
            {/* Section 1 - Agency */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <h2 
                className={`${content.about_roel_title_size_1 || 'text-4xl'} font-bold text-gray-900 mb-4`}
                style={{ fontFamily: `'${content.about_roel_title_font_1 || 'Inter'}', sans-serif` }}
              >
                {renderTextWithBreaks(content.about_roel_title_1 || 'ROEL')}
              </h2>
              <h3 
                className={`${content.about_roel_subtitle_size_1 || 'text-2xl'} font-semibold text-gray-700 mb-6`}
                style={{ fontFamily: `'${content.about_roel_subtitle_font_1 || 'Inter'}', sans-serif` }}
              >
                {renderTextWithBreaks(content.about_roel_subtitle_1 || 'The Art Director')}
              </h3>
              
              {content.about_roel_content_type_1 === 'logos' && agencyLogos.length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  {agencyLogos.map((logo: string, index: number) => (
                    <div key={index} className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                      <img 
                        src={logo} 
                        alt={`Agency logo ${index + 1}`}
                        className="max-w-full h-auto max-h-16 object-contain grayscale hover:grayscale-0 transition-all"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p 
                  className={`${content.about_roel_text_size_1 || 'text-base'} text-gray-600 leading-relaxed`}
                  style={{ fontFamily: `'${content.about_roel_text_font_1 || 'Inter'}', sans-serif` }}
                >
                  {renderTextWithBreaks(content.about_roel_text_1 || 'WITH AN EYE FOR DETAIL AND A PASSION FOR VISUAL STORYTELLING...')}
                </p>
              )}
            </div>

            {/* Section 2 - Brands */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <h2 
                className={`${content.about_roel_title_size_2 || 'text-4xl'} font-bold text-gray-900 mb-4`}
                style={{ fontFamily: `'${content.about_roel_title_font_2 || 'Inter'}', sans-serif` }}
              >
                {renderTextWithBreaks(content.about_roel_title_2 || 'WIETSE')}
              </h2>
              <h3 
                className={`${content.about_roel_subtitle_size_2 || 'text-2xl'} font-semibold text-gray-700 mb-6`}
                style={{ fontFamily: `'${content.about_roel_subtitle_font_2 || 'Inter'}', sans-serif` }}
              >
                {renderTextWithBreaks(content.about_roel_subtitle_2 || 'The Copywriter')}
              </h3>
              
              {content.about_roel_content_type_2 === 'logos' && brandsLogos.length > 0 ? (
                <div className="grid grid-cols-2 gap-4">
                  {brandsLogos.map((logo: string, index: number) => (
                    <div key={index} className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                      <img 
                        src={logo} 
                        alt={`Brand logo ${index + 1}`}
                        className="max-w-full h-auto max-h-16 object-contain grayscale hover:grayscale-0 transition-all"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p 
                  className={`${content.about_roel_text_size_2 || 'text-base'} text-gray-600 leading-relaxed`}
                  style={{ fontFamily: `'${content.about_roel_text_font_2 || 'Inter'}', sans-serif` }}
                >
                  {renderTextWithBreaks(content.about_roel_text_2 || 'WORDS ARE MY CRAFT, STORIES MY PASSION...')}
                </p>
              )}
            </div>

            {/* Section 3 - Awards */}
            <div className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
              <h2 
                className={`${content.about_roel_title_size_3 || 'text-4xl'} font-bold text-gray-900 mb-4`}
                style={{ fontFamily: `'${content.about_roel_title_font_3 || 'Inter'}', sans-serif` }}
              >
                {renderTextWithBreaks(content.about_roel_title_3 || 'TOGETHER')}
              </h2>
              <h3 
                className={`${content.about_roel_subtitle_size_3 || 'text-2xl'} font-semibold text-gray-700 mb-6`}
                style={{ fontFamily: `'${content.about_roel_subtitle_font_3 || 'Inter'}', sans-serif` }}
              >
                {renderTextWithBreaks(content.about_roel_subtitle_3 || 'The Creative Duo')}
              </h3>
              <p 
                className={`${content.about_roel_text_size_3 || 'text-base'} text-gray-600 leading-relaxed`}
                style={{ fontFamily: `'${content.about_roel_text_font_3 || 'Inter'}', sans-serif` }}
              >
                {renderTextWithBreaks(content.about_roel_text_3 || 'COMBINING VISUAL EXCELLENCE WITH COMPELLING NARRATIVES...')}
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
